import {
  type ConnectionVerification,
  verificationMessage,
  verificationSeverity,
  verificationTitle
} from '../connection-verification.js';
/**
 * AuthenticationContent — settings tab for editing device credentials.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). The
 * Authentication tab locks:
 *
 *   - Tab is the deep-link target for the chip's "Authenticate"
 *     verb (`<NeedsAttentionChip targetTab='authentication'>`).
 *   - Tab is ALWAYS visible (regardless of `isEmbeddedContext`).
 *   - Submit DISPATCHES by `transitoryKind` (Multi-Add Phase 2, spec
 *     §4.2 / §5). The device the user is authenticating is one of three
 *     shapes, discriminated by the transitory-placeholder slice
 *     (`#~/device/transitory.js`) keyed on the candidate id:
 *       · `onvif` placeholder → `probeOnvifDevice` then commit the REAL
 *         RTSP device (creds STAMPED into the video config per VC-A61P,
 *         §5.1) and remove the placeholder.
 *       · `iris`  placeholder → `linkIrisDevice`; on success remove the
 *         placeholder (real device(s) arrive via state-sync, §5.2).
 *       · neither (a real device with bad creds) → the UNCHANGED
 *         `applyCredentialsToCandidate` fold (Phase 1 behavior, §4.2).
 *   - Embedded gate is INVERTED for placeholders (§4.2): a real
 *     Iris-enabled device that is already linked stays locked (re-link is
 *     out of scope, §12) and keeps the "Iris re-link is not supported…"
 *     copy; a transitory placeholder is editable so the user can
 *     authenticate it.
 *
 * Per `think-in-systems` (ai.knowledge/global): the dispatch decision +
 * the ONVIF post-probe commit are extracted into `dispatchAuthSubmit`
 * (pure, fully dependency-injected) so every branch is unit-testable with
 * DI fakes for `probeOnvifDevice` / `linkIrisDevice` (per
 * `ai-code-requires-tests` + `esm-jest-mock-patterns`) without rendering
 * the component or booting the `@studio/models/device` tree.
 *
 * Per `stay-in-scope` (ai.knowledge/global): the ONVIF commit reuses the
 * existing bulk-add success path — `buildBulkCandidate` + creds-stamp +
 * VISCA/IP control + `addDevice` (mirrors `add/wiring.ts:995-1024,1331-1338`).
 * No new credential-propagation or commit logic.
 */
import { applyCredentialsToCandidate } from '#~/video/components/add/source/utils.js';
import { type TransientsSnapshot } from '#~/video/components/add/source/onvif-probe.js';
import { setTransitoryAuthTabOpen, useTransitoryDevice } from '#~/device/transitory.js';
import { dispatchAuthSubmit, IRIS_DESKTOP_ONLY_MESSAGE } from './auth-dispatch.js';
import { depot } from '#~/depot/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { Slicer } from '#~/depot/slicer.js';
import { ElectronAPI } from '#~/electron/api.js';
import { isElectronApp } from '#~/electron/utils.js';
import { useAuth0 } from '#~/auth/index.js';
import { createDeviceLifecycleTracker } from '#~/analytics/domains/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { discoveryDeviceAddProperties } from '#~/video/components/add/device-add-analytics.js';
import { Caveat, Credentials } from '#~/common/editor/index.js';
import { migrateTransitoryOriginToReal } from '#~/notifications/migrate-transitory-origin.js';
import type { Device } from '#~/device/index.js';
import { Button } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import { usePrompt } from '@studio/design-system/prompt';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import type { Agent } from '@studio/models/agent';
import { isTransient } from '@studio/models/device';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import { twMerge } from 'cn';
import { dequal } from 'dequal';
import { Sel, STag } from '#~/stag/index.js';
import type { ReactElement } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
// Match the Video tab layout: settings on the left column + live
// video preview on the right column. The `Columns` primitive used
// here is the settings-scoped variant (`grid-cols-[repeat(2,350px)]
// gap-[80px]`), not the generic editor `Columns` — same primitive the
// Video tab uses, so the auth surface mirrors the Video surface 1:1.
// Per `stay-in-scope` (ai.knowledge/global): reuse the existing
// settings layout primitive instead of rolling a new grid.
import { Columns } from '../layout.jsx';
import { Preview } from '../preview.jsx';
import { useProducer, useStore } from '../store/hooks.js';

/** Props for AuthenticationContent component */
interface AuthenticationContentProps {
  agent?: Agent | undefined;
  className?: string;
  onCredentialsSaved?: () => void;
}

/**
 * AuthenticationContent component for managing device credentials.
 *
 * Renders username + password inputs and a submit button. On submit,
 * `dispatchAuthSubmit` routes by `transitoryKind` (spec §4.2): ONVIF/Iris
 * placeholders authenticate against the camera and remove the placeholder
 * on success; a real device with bad creds folds credentials into the
 * settings candidate via the UNCHANGED Phase-1 path.
 *
 * The embedded gate is INVERTED for placeholders: a real already-linked
 * Iris device stays locked with the "re-link not supported" copy (§9/§12);
 * a placeholder is editable so the user can authenticate it.
 */
export const AuthenticationContent = ({
  agent,
  className,
  onCredentialsSaved
}: AuthenticationContentProps): ReactElement => {
  const producer = useProducer();
  // Bug 2 (spec §2.3): the design-system prompt used to raise the single-add
  // relink confirmation over the same AgentRegistryLink call.
  const prompt = usePrompt();
  // Keep authentication scoped to the device this editor owns.
  const deviceId = useStore((µ) => µ.candidate.id);
  // The device's agent id (settings store) — ONVIF probe agent fallback.
  const candidateAgentId = useStore((µ) => µ.agentId);
  const onDeviceCommitted = useStore((µ) => µ.onDeviceCommitted);
  // The frozen original device (spec §4.3): the real-device reload builds its
  // credentials-only replacement from THIS, not the live candidate. Optional-
  // chained — some mount paths (e.g. the label-mount render tests) omit
  // `lifecycle` from their store fixture.
  const original = useStore((µ) => µ.lifecycle?.original);
  // Footer Save holds this while replaceDevice runs; Submit must not overlap.
  const isBusy = useStore((µ) => µ.flags?.isBusy === true);
  const [credentials, setCredentials] = useState<M_Credentials>();
  // Inline auth error (spec §5): set on a failed probe/link/commit/reload; the
  // placeholder or device stays and the user can retry.
  const [error, setError] = useState<string>();
  // Inline success confirmation (spec §4.2/§4.3): every non-error terminal
  // kind reports here so Submit is never a silent no-op.
  const [success, setSuccess] = useState<string>();
  // Saved but only partly confirmed (working video, unconfirmed control) or not confirmed at all:
  // yellow, never red — a rejection is the only credential outcome shown as an error.
  const [warning, setWarning] = useState<{ title: string; message: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Credentials the latest Submit verified; cleared when a new Submit starts so a failed attempt is never masked.
  const [verifiedCredentials, setVerifiedCredentials] = useState<M_Credentials>();

  // Bug 2 (spec §2.3, rule `settle-every-terminal-branch`): the resolver of an
  // in-flight relink confirmation. `confirmRelink` awaits `usePrompt().confirm`,
  // which never fires `onConfirm` if the settings panel unmounts mid-confirm —
  // so track the pending resolver and settle it as cancel on unmount. Without
  // this the awaited Submit promise hangs and the busy state never resets.
  const pendingRelinkResolve = useRef<((confirmed: boolean) => void) | null>(null);
  useEffect(
    () => () => {
      // Panel closed mid-confirm: settle as cancel so dispatchAuthSubmit
      // resolves (→ already-linked bail) instead of hanging.
      pendingRelinkResolve.current?.(false);
      pendingRelinkResolve.current = null;
    },
    []
  );

  // Look the candidate up in the transitory-placeholder slice (W2). A
  // placeholder means this is an unauthenticated bulk-add camera; its
  // `transitoryKind` drives the submit dispatch (spec §4.2).
  const placeholder = useTransitoryDevice(deviceId);
  const isTransitory = !!placeholder;

  // Liveness GUARD (spec §7, CRITICAL race): while THIS placeholder's
  // Authentication tab is open, proactive eviction must not remove it — the
  // camera must not vanish under the user mid-submit. Marking it also
  // cancels any in-flight absence debounce; clearing on unmount lets the
  // next discovery tick re-arm it if the discovery is still gone.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  useEffect(() => {
    if (!isTransitory || !deviceId) {
      return;
    }

    setTransitoryAuthTabOpen(deviceId);

    return () => setTransitoryAuthTabOpen(null);
  }, [isTransitory, deviceId]);

  // Iris-enabled (embedded) devices: a REAL already-linked device stays
  // locked (re-link out of scope, §12). A transitory placeholder is NOT
  // locked even when the agent looks embedded — the user must be able to
  // authenticate it (gate inversion, §4.2).
  const isEmbedded = !!agent?.embedded;
  const isLocked = isEmbedded && !isTransitory;
  // A pending Iris camera can only be linked from the desktop app.
  const needsDesktopApp = placeholder?.transitoryKind === 'iris' && !isElectronApp();

  const { getAccessTokenSilently } = useAuth0();
  const { track } = useAnalytics();
  const appId = useDepot((µ) => µ.appId);
  // roomId resolution (Multi-Add Phase 2 — fixes "No room available to
  // authenticate against"): a transitory Iris placeholder has NO joined
  // embedded agent yet, so `agent?.roomId` is undefined and the
  // `dispatchAuthSubmit` roomId guard rejects Submit. Fall back to the
  // settings store's roomId — the room the dialog was opened against,
  // threaded `useSettings` → `SettingsWrapper` → `Settings` → store. A real
  // already-linked device keeps using `agent.roomId` (the store value is the
  // same room, so the fallback is a no-op for it).
  // Per `stay-in-scope` + `reference-rules-in-comments` (ai.knowledge/global).
  const storeRoomId = useStore((µ) => µ.roomId);
  const roomId = agent?.roomId ?? storeRoomId;

  const formId = useId();
  const canSubmit =
    !isLocked && !needsDesktopApp && !!credentials?.username && !isSubmitting && !isBusy;
  // Unchanged, just-verified credentials need no second verification; editing them re-arms Submit.
  const verified = !!verifiedCredentials && dequal(credentials, verifiedCredentials);
  // A native form gives Submit, Enter, and the footer exactly the same verification path.
  useEffect(() => {
    producer((draft) => {
      draft.authenticationForm = { id: formId, canSubmit, verified };
    });
    return () => {
      producer((draft) => {
        // Zustand merges root state: clear explicitly so an unmounted form cannot survive the merge.
        if (draft.authenticationForm?.id === formId) draft.authenticationForm = undefined;
      });
    };
  }, [formId, canSubmit, verified, producer]);

  const handleSubmit = useLatestFn(async () => {
    // Both buttons and Enter share the same locked/empty/busy guard.
    if (!canSubmit) {
      return;
    }

    setError(undefined);
    setSuccess(undefined);
    setWarning(undefined);
    setVerifiedCredentials(undefined);
    setIsSubmitting(true);
    // Hold the footer's global busy flag across the ENTIRE submit so the footer
    // "Save Changes" cannot fire a second, overlapping replaceDevice on the
    // same device while this credentials reload is in flight. force:true
    // bypasses the add single-flight guard (device/commands/add.ts), so this
    // shared busy flag is what actually serializes Submit vs Save. Reset in the
    // `finally`. Per `think-in-systems` (ai.knowledge/global).
    producer((draft) => {
      draft.flags.isBusy = true;
      draft.verification = { step: 'authentication', status: 'pending' };
    });

    try {
      if (isLocked) {
        // Real already-linked Iris device — re-link deferred (§12). No-op.
        return;
      }

      const token = await getAccessTokenSilently();

      // Transients accessor for the ONVIF probe: the bridge publishes the
      // transient probe device into `room.devices`; mirror the add-store
      // provider's derivation (filter `room.devices` by `isTransient`).
      const transients: TransientsSnapshot = {
        read: () => {
          if (!roomId) return undefined;
          try {
            const devices = new Slicer(depot.getState()).room(roomId).devices;
            return Object.fromEntries(
              Object.entries(devices).filter(([id]) => isTransient(id as Device['id']))
            );
          } catch {
            // Room not resolved yet — the probe just keeps polling.
            return undefined;
          }
        }
      };

      const result = await dispatchAuthSubmit(
        placeholder,
        credentials,
        // else-branch fold (UNCHANGED Phase-1 behavior, §4.2): the helper
        // handles ONVIF/HTTP/VISCA_IP control + RTSP-family video.
        () =>
          producer(({ candidate }) => {
            applyCredentialsToCandidate(candidate, credentials);
          }),
        {
          roomId,
          token,
          appId,
          studioAgentId: candidateAgentId,
          transients,
          // Real-device reload input (spec §4.3): the frozen original the
          // credentials-only replacement is cloned from. The dispatcher defaults
          // the `replaceRealDevice` seam itself (same as probeOnvif/linkIris).
          original,
          // Bug 2 (spec §2.3): let an already-linked embedded camera relink from
          // Advanced Settings. Reuses the single-add confirmation VERBATIM — same
          // copy, same 'Link Bridge with this Studio' okay-label, same danger
          // variant (credentials-dialog.tsx:145-169). The byte-identical label is
          // a HIL contract: the fixture selects
          // getByRole('button',{name:'Link Bridge with this Studio'}), so it picks
          // this prompt up for free (spec §2.3). Wrapped in a promise the same way
          // as `promptUserToUnlink` (search-devices.tsx). link-iris invokes it
          // only on the already-linked branch; on confirm it falls through to the
          // same linkAgent, and the `linked` success arm reloads (below).
          confirmRelink: () =>
            new Promise<boolean>((resolve) => {
              // Settle exactly once — whichever of onConfirm / unmount fires first
              // wins; the loser is a no-op (rule `settle-every-terminal-branch`).
              const settle = (confirmed: boolean): void => {
                if (pendingRelinkResolve.current !== resolve) return;
                pendingRelinkResolve.current = null;
                resolve(confirmed);
              };
              pendingRelinkResolve.current = resolve;
              prompt.confirm({
                title: 'Unlink Bridge',
                message: [
                  'This device is currently linked to another Studio.',
                  'By continuing, it will be unlinked from the existing Studio and linked with this one.'
                ].join('\n'),
                okay: { variant: 'danger', label: 'Link Bridge with this Studio' },
                cancel: { label: 'Cancel' },
                onConfirm: (confirmed) => settle(confirmed)
              });
            }),
          // ENG-4950: the camera now counts as added, through the same
          // device_add tracker single add uses.
          onCameraSaved: (device) =>
            createDeviceLifecycleTracker(track).trackDeviceAddSuccess(
              discoveryDeviceAddProperties(device, roomId)
            )
        }
      );

      if (result.kind === 'error') {
        // Inline failure (spec §5 / §4.2): the placeholder or device stays and
        // the user can retry. Covers the reload's throw, no-live-device, and
        // add-after-remove partial as well as the probe/link failures.
        setError(result.message);
        return;
      }

      // Success — every terminal kind gets explicit feedback (no silent arm,
      // rule `settle-every-terminal-branch`):
      //   · linked   — a new embedded agent joined; reload so the app re-reads
      //                the device(s) it announces via state-sync (UNCHANGED
      //                transitory behavior — byte-identical to before).
      //   · reloaded — the real device was committed + reloaded in place; the
      //                tile chip re-derives from the fresh bridge state-sync.
      //   · committed/applied — device added / creds folded into the draft.
      // One consistent inline confirmation for the reactive kinds; `linked`
      // keeps its functionally-required studio reload (V4.3 — documented in PR).
      setError(undefined);

      // ENG-4486 Fix A.3: the ONVIF placeholder authenticated and committed as a
      // REAL device (`committed` carries the fresh real id). Migrate the
      // multi-add origin from the placeholder id to the real id so the post-add
      // observer keeps firing for the REAL device (e.g. a still-missing model →
      // "Select Model", progressive resolution) and clear the now-stale
      // transitory "Authenticate" toast. No-op for wizard/single-add (no origin
      // registered) and for the real-device (`!placeholder`) path. Iris is
      // deferred (see auth-dispatch.ts). Per `reference-rules-in-comments`
      // (ai.knowledge/global).
      if (result.kind === 'committed' && placeholder) {
        migrateTransitoryOriginToReal(placeholder.id, result.deviceId);
        onDeviceCommitted?.(result.device);
      }

      // Only an explicit rejection (or a failure working video cannot vouch for) is red, and it
      // names the aspect that rejected. Partial verification falls through as a saved commit.
      if (result.kind === 'committed' && verificationSeverity(result.verification) === 'error') {
        setError(verificationMessage(result.verification, result.verificationAspects));
        producer((draft) => {
          draft.verification = { step: 'authentication', status: result.verification };
        });
        return;
      }

      if (result.kind === 'linked') {
        if (isElectronApp()) {
          ElectronAPI.onReloadStudio();
        }
        window.location.reload();
        return;
      }

      if (result.kind === 'reloaded') {
        // Credentials-only: stamp the submitted creds onto the EXISTING snapshot
        // so unrelated video edits stay dirty and Save can persist them. Copying
        // the whole candidate would mark those edits as saved without sending
        // them. If the only delta was creds, snapshot matches candidate and
        // Save stays disabled (no second replaceDevice).
        producer((draft) => {
          applyCredentialsToCandidate(draft.lifecycle.snapshot, credentials);
          if (result.resolvedVideo) {
            // Keep unrelated draft edits while replacing the pending camera's provisional stream.
            if (dequal(draft.candidate.video, draft.lifecycle.snapshot.video)) {
              draft.candidate.video = result.resolvedVideo;
            }
            draft.lifecycle.snapshot.video = result.resolvedVideo;
            if (draft.lifecycle.original?.video) {
              draft.lifecycle.original.video.config = result.resolvedVideo;
            }
          }
          const videoStillDirty = !dequal(draft.candidate, draft.lifecycle.snapshot);
          draft.flags.isVideoSettingsDirty = videoStillDirty;
          draft.flags.isDirty =
            videoStillDirty || draft.flags.isDeviceSettingsDirty || draft.flags.isNameDirty;
        });
      }

      // `reloaded` without a verification verdict means the camera confirmed everything.
      const status: ConnectionVerification =
        result.kind === 'applied' ? 'unverified' : (result.verification ?? 'verified');
      const aspects = result.kind === 'applied' ? undefined : result.verificationAspects;
      if (result.kind === 'applied') {
        setSuccess('Credentials have not been verified. Save changes to connect to the camera.');
      } else if (status === 'verified') {
        setSuccess('Connection verified.');
      } else {
        // Saved, and not rejected: working video is acknowledged, unresolved control is a warning.
        setWarning({
          title: verificationTitle(status),
          message: verificationMessage(status, aspects)
        });
      }
      // The footer can now finish instead of verifying these exact credentials again. Working
      // video already proves the camera accepts them; a wholly unconfirmed save stays retryable.
      if (status === 'verified' || status === 'video-verified') setVerifiedCredentials(credentials);
      producer((draft) => {
        draft.verification = { step: 'authentication', status };
      });
      // Continue setup only after credentials were persisted, never after a failed submit.
      if (result.kind === 'reloaded' || result.kind === 'committed') {
        onCredentialsSaved?.();
      }
    } catch {
      setError('Could not verify the camera connection. Check the connection and try again.');
    } finally {
      // Always settle the local + store busy state, on every terminal branch
      // (success, inline error, or a throw). Per `settle-every-terminal-branch`.
      setIsSubmitting(false);
      producer((draft) => {
        draft.flags.isBusy = false;
        if (draft.verification?.status === 'pending') draft.verification.status = 'unverified';
      });
    }
  });

  return (
    <form
      id={formId}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      {...STag(Sel.VideoSettings, 'authentication', deviceId)}
      className={twMerge('flex flex-col gap-24', className)}
    >
      {/* Two-column layout mirroring the Video tab: credentials form on
          the left, the live device preview on the right. */}
      <Columns>
        <Columns.Column>
          <h3>Credentials</h3>

          {isLocked ? (
            // Re-link of a REAL already-linked Iris device is out of scope
            // (§12). Keep the existing user-facing copy verbatim (§9).
            <Caveat title='Iris re-link is not supported in this release. To re-pair an Iris-enabled device, contact your administrator.' />
          ) : needsDesktopApp ? (
            <Caveat title={IRIS_DESKTOP_ONLY_MESSAGE} />
          ) : (
            <Caveat title='Update the credentials used to authenticate with this device.' />
          )}

          <Credentials credentials={toAccessor(credentials, setCredentials)} />

          {error && (
            // Inline auth error (spec §5): placeholder stays, retryable.
            // `text-error` honors whitelabel variants (Iris/Hive) per
            // `ui-architecture` (ai.knowledge/global).
            <p className='text-sm text-error' role='alert'>
              {error}
            </p>
          )}

          {warning && (
            // Yellow per `ui-architecture` (ai.knowledge/global): the design-system Callout maps
            // severity to the whitelabel `warning` tokens, same as the section's driver warning.
            <Callout severity='warning' title={warning.title} role='status'>
              <Callout.P>{warning.message}</Callout.P>
            </Callout>
          )}

          {success && (
            // Inline success confirmation (spec §4.2/§4.3): mirrors the error
            // arm so every non-error terminal kind gives explicit feedback.
            <p className='text-sm' role='status'>
              {success}
            </p>
          )}

          <Button
            {...STag(Sel.VideoSettings, 'authenticate-submit')}
            className='self-end'
            disabled={!canSubmit}
            type='submit'
          >
            {isSubmitting ? 'Verifying...' : 'Submit'}
          </Button>
        </Columns.Column>

        <Columns.Column>
          <h3>Video preview</h3>
          <Preview />
        </Columns.Column>
      </Columns>
    </form>
  );
};

AuthenticationContent.displayName = 'AuthenticationContent';
